import {
  Button,
  ButtonGroup,
  Chip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Selection,
} from '@heroui/react';
import React from 'react';
import { FiCheck, FiChevronDown, FiDatabase, FiFilter } from 'react-icons/fi';

import { usePuppyMode, PuppyFilterMode } from '../../contexts/PuppyModeContext';

export interface PuppyModeToggleProps {
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Size variant for the toggle buttons
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Whether to show as a compact dropdown instead of full buttons
   * @default true
   */
  compact?: boolean;
}

/**
 * PuppyModeToggle - Toggle component for switching between standard and dogfood users
 *
 * This component provides a toggle interface for filtering between standard users
 * and dogfood users. It's only visible in non-production environments
 * for safety and testing purposes.
 *
 * Features:
 * - Two modes: Standard users, Dogfood users
 * - Only visible in non-production environments
 * - Integration with usePuppyMode hook for state management
 */
export const PuppyModeToggle: React.FC<PuppyModeToggleProps> = ({
  className = '',
  size = 'sm',
  compact = true,
}) => {
  const { isPuppyModeAvailable, filterMode, setFilter, environment } =
    usePuppyMode();

  // Don't render in production environments
  if (!isPuppyModeAvailable) {
    return null;
  }

  const activeMode: Exclude<PuppyFilterMode, 'all'> =
    filterMode === 'puppy' ? 'puppy' : 'normal';

  const modeCopy = {
    normal: {
      label: 'Standard',
      menuLabel: 'Standard users',
      helper: 'Hide dogfood accounts',
      icon: <FiDatabase className="h-4 w-4" />,
    },
    puppy: {
      label: 'Dogfood',
      menuLabel: 'Dogfood users',
      helper: 'Show only dogfood accounts',
      icon: <FiFilter className="h-4 w-4" />,
    },
  };

  if (compact) {
    const handleSelectionChange = (keys: Selection) => {
      if (keys === 'all') return;

      const selectedKey = Array.from(keys)[0] as string;
      if (selectedKey) {
        const selectedMode: PuppyFilterMode =
          selectedKey === 'puppy' ? 'puppy' : 'normal';
        setFilter(selectedMode);
      }
    };

    const activeCopy = modeCopy[activeMode];
    const isDogfood = activeMode === 'puppy';

    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Dropdown>
          <DropdownTrigger>
            <Button
              variant="flat"
              color={isDogfood ? 'warning' : 'default'}
              className={`h-9 min-w-0 gap-2 rounded-full border px-3 text-default-700 transition-all ${
                isDogfood
                  ? 'border-warning-200 bg-warning-50 text-warning-700 hover:bg-warning-100 dark:border-warning-500/30 dark:bg-warning-400/15 dark:text-warning-300'
                  : 'border-default-200 bg-default-100 hover:bg-default-200 dark:border-default-700 dark:bg-default-100/10 dark:hover:bg-default-100/20'
              }`}
              size={size}
              aria-label={`User scope: ${activeCopy.menuLabel}`}
            >
              {activeCopy.icon}
              <span className="hidden text-xs font-semibold leading-none lg:inline">
                {activeCopy.label}
              </span>
              <FiChevronDown className="hidden h-3.5 w-3.5 opacity-60 lg:block" />
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            aria-label="User scope options"
            selectionMode="single"
            selectedKeys={new Set([activeMode])}
            onSelectionChange={handleSelectionChange}
            className="min-w-56"
          >
            <DropdownItem
              key="normal"
              className="p-3"
              startContent={modeCopy.normal.icon}
              endContent={
                activeMode === 'normal' ? (
                  <FiCheck className="h-4 w-4 text-primary" />
                ) : null
              }
              textValue={modeCopy.normal.label}
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold">
                  {modeCopy.normal.menuLabel}
                </span>
                <span className="text-xs text-default-500">
                  {modeCopy.normal.helper}
                </span>
              </div>
            </DropdownItem>
            <DropdownItem
              key="puppy"
              className="p-3"
              startContent={modeCopy.puppy.icon}
              endContent={
                activeMode === 'puppy' ? (
                  <FiCheck className="h-4 w-4 text-warning-600" />
                ) : null
              }
              textValue={modeCopy.puppy.label}
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold">
                  {modeCopy.puppy.menuLabel}
                </span>
                <span className="text-xs text-default-500">
                  {modeCopy.puppy.helper}
                </span>
              </div>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className="text-sm text-default-500 font-medium">Filter:</span>
      <ButtonGroup size={size} isDisabled={false}>
        <Button
          variant={
            filterMode === 'normal' || filterMode === 'all'
              ? 'solid'
              : 'bordered'
          }
          color={
            filterMode === 'normal' || filterMode === 'all'
              ? 'primary'
              : 'default'
          }
          onPress={() => setFilter('normal')}
          className="min-w-0"
          startContent={modeCopy.normal.icon}
        >
          Standard users
        </Button>
        <Button
          variant={filterMode === 'puppy' ? 'solid' : 'bordered'}
          color={filterMode === 'puppy' ? 'warning' : 'default'}
          onPress={() => setFilter('puppy')}
          className="min-w-0"
          startContent={modeCopy.puppy.icon}
        >
          Dogfood users
        </Button>
      </ButtonGroup>

      {/* Environment indicator */}
      <Chip
        color="warning"
        variant="flat"
        size="sm"
        className="text-xs"
        title={`${environment.name} Environment`}
      >
        {environment.emoji} {environment.name}
      </Chip>
    </div>
  );
};
